import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { FileText, Eye, Edit2 } from 'lucide-react';

/**
 * RichTextBlockEditor - Admin editor for Rich Text blocks
 * Features:
 * - Textarea for Markdown input
 * - Live preview toggle
 * - Bilingual support (TH/EN)
 */
const RichTextBlockEditor = ({ block, onUpdate }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('en');
  
  const config = block?.config || {};
  const contentEn = config.content_en || '';
  const contentTh = config.content_th || '';

  const handleContentChange = (language, value) => {
    const newConfig = {
      ...config,
      [`content_${language}`]: value
    };
    onUpdate(newConfig);
  };

  const currentContent = activeLanguage === 'th' ? contentTh : contentEn;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-700">Rich Text Block</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Language Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setActiveLanguage('en')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeLanguage === 'en'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setActiveLanguage('th')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeLanguage === 'th'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              TH
            </button>
          </div>
          
          {/* Preview Toggle */}
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className={`px-3 py-1 text-sm rounded-md border transition-colors ${
              showPreview
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {showPreview ? (
              <>
                <Edit2 className="w-4 h-4 inline mr-1" />
                Edit
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 inline mr-1" />
                Preview
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content Editor */}
      {showPreview ? (
        /* Preview Mode */
        <div className="bg-gray-50 rounded-lg p-4 min-h-[200px]">
          {currentContent ? (
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown>{currentContent}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-gray-400 italic text-sm">
              No content in {activeLanguage === 'th' ? 'Thai' : 'English'}
            </p>
          )}
        </div>
      ) : (
        /* Edit Mode */
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content ({activeLanguage === 'th' ? 'Thai' : 'English'}) - Markdown supported
          </label>
          <textarea
            rows={8}
            value={currentContent}
            onChange={(e) => handleContentChange(activeLanguage === 'th' ? 'th' : 'en', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder={activeLanguage === 'th' 
              ? 'กรอกรายละเอียดในรูปแบบ Markdown...\n\nตัวอย่าง:\n# หัวข้อ\n- รายการ 1\n- รายการ 2'
              : 'Enter content in Markdown format...\n\nExample:\n# Heading\n- Item 1\n- Item 2'}
          />
          <p className="mt-2 text-xs text-gray-500">
            Supports Markdown: **bold**, *italic*, # headings, - lists, [links](url)
          </p>
        </div>
      )}

      {/* Content Status Indicator */}
      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
        <span>
          EN: {contentEn.length > 0 ? `${contentEn.length} chars` : 'Empty'}
        </span>
        <span>
          TH: {contentTh.length > 0 ? `${contentTh.length} chars` : 'Empty'}
        </span>
      </div>
    </div>
  );
};

export default RichTextBlockEditor;
